import { useState, useEffect, useCallback } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Edit, Plus, Trash2, Package, Info, Star, Clock, Loader2, Euro, Boxes, Wrench } from "lucide-react";
import { toast } from "sonner";
import ProductFormDialog from "./ProductFormDialog";
import ProductPricesDialog from "./ProductPricesDialog";
import ProductStockDialog from "./ProductStockDialog";
import { SupplierLinkedEquipmentTab } from "./SupplierLinkedEquipmentTab";
import { SEVERITY_CONFIG } from "@/lib/supplierHelpers";

export const SupplierDetailView = ({ supplier, onBack, onEdit, onDeleted }) => {
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productDialog, setProductDialog] = useState({ open: false, product: null });
  const [priceDialog, setPriceDialog] = useState({ open: false, product: null });
  const [stockDialog, setStockDialog] = useState({ open: false, product: null });
  const [eventDialog, setEventDialog] = useState({ open: false, event: null });
  const [eventForm, setEventForm] = useState({
    event_date: "", event_time: "", title: "", comment: "", severity: "neutral",
  });

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [pRes, eRes] = await Promise.all([
        api.getSupplierProducts(supplier.id),
        api.getSupplierLifeEvents(supplier.id),
      ]);
      setProducts(pRes.data);
      setEvents(eRes.data);
    } catch (_e) {
      toast.error("Erreur chargement fournisseur");
    } finally {
      setLoading(false);
    }
  }, [supplier.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Supprimer ce produit et tout son historique de prix ?")) return;
    try {
      await api.deleteSupplierProduct(id);
      toast.success("Produit supprimé");
      fetchAll();
    } catch (_e) {
      toast.error("Erreur de suppression");
    }
  };

  const handleDeleteSupplier = async () => {
    try {
      await api.deleteSupplier(supplier.id);
      toast.success("Fournisseur supprimé");
      onDeleted();
    } catch (_e) {
      toast.error("Erreur de suppression");
    }
  };

  const openCreateEvent = () => {
    setEventForm({
      event_date: new Date().toISOString().slice(0, 10),
      event_time: "", title: "", comment: "", severity: "neutral",
    });
    setEventDialog({ open: true, event: null });
  };

  const openEditEvent = (ev) => {
    setEventForm({
      event_date: ev.event_date || "",
      event_time: ev.event_time || "",
      title: ev.title || "",
      comment: ev.comment || "",
      severity: ev.severity || "neutral",
    });
    setEventDialog({ open: true, event: ev });
  };

  const saveEvent = async () => {
    if (!eventForm.event_date || !eventForm.comment.trim()) {
      toast.error("Date et commentaire requis");
      return;
    }
    try {
      const payload = {
        event_date: eventForm.event_date,
        event_time: eventForm.event_time || null,
        title: eventForm.title,
        comment: eventForm.comment,
        severity: eventForm.severity,
      };
      if (eventDialog.event) {
        await api.updateSupplierLifeEvent(eventDialog.event.id, payload);
        toast.success("Événement mis à jour");
      } else {
        await api.createSupplierLifeEvent(supplier.id, payload);
        toast.success("Événement ajouté");
      }
      setEventDialog({ open: false, event: null });
      fetchAll();
    } catch (_e) {
      toast.error("Erreur de sauvegarde");
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    try {
      await api.deleteSupplierLifeEvent(id);
      toast.success("Événement supprimé");
      fetchAll();
    } catch (_e) {
      toast.error("Erreur de suppression");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="supplier-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="back-to-suppliers">
            <ArrowLeft className="h-4 w-4 mr-2" /> Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{supplier.company_name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline">{products.length} produit(s)</Badge>
              {supplier.siret && <Badge variant="outline" className="font-mono text-xs">SIRET {supplier.siret}</Badge>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onEdit}><Edit className="h-4 w-4 mr-2" /> Modifier</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-red-600 hover:text-red-700">
                <Trash2 className="h-4 w-4 mr-2" /> Supprimer
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer ce fournisseur ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Tous ses produits, historiques de prix et événements marquants seront également supprimés. Action irréversible.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteSupplier} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Supprimer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info" data-testid="sup-tab-info"><Info className="h-4 w-4 mr-1" /> Informations</TabsTrigger>
          <TabsTrigger value="products" data-testid="sup-tab-products">
            <Package className="h-4 w-4 mr-1" /> Produits
            {products.length > 0 && <Badge variant="outline" className="ml-1 h-5 min-w-5 text-xs px-1">{products.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="events" data-testid="sup-tab-events"><Star className="h-4 w-4 mr-1" /> Événements marquants</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="sup-tab-timeline"><Clock className="h-4 w-4 mr-1" /> Chronologie</TabsTrigger>
          <TabsTrigger value="equipment" data-testid="sup-tab-equipment"><Wrench className="h-4 w-4 mr-1" /> Parc matériel</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle>Coordonnées (siège / par défaut)</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div><span className="text-muted-foreground">Adresse :</span> {supplier.address || "-"}</div>
                <div><span className="text-muted-foreground">Téléphone :</span> {supplier.phone || "-"}</div>
                <div><span className="text-muted-foreground">Email :</span> {supplier.email || "-"}</div>
                <div><span className="text-muted-foreground">Site web :</span> {supplier.website ? <a href={supplier.website} target="_blank" rel="noreferrer" className="text-primary underline">{supplier.website}</a> : "-"}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Administratif</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div><span className="text-muted-foreground">SIRET :</span> <span className="font-mono">{supplier.siret || "-"}</span></div>
                <div><span className="text-muted-foreground">N° TVA :</span> <span className="font-mono">{supplier.tva_number || "-"}</span></div>
              </CardContent>
            </Card>
            <Card className="md:col-span-2">
              <CardHeader><CardTitle>Notes</CardTitle></CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{supplier.notes || "Aucune note."}</p>
              </CardContent>
            </Card>

            {/* --- Agences --- */}
            <Card className="md:col-span-2" data-testid="supplier-branches-card">
              <CardHeader><CardTitle>Agences ({(supplier.branches || []).length})</CardTitle></CardHeader>
              <CardContent>
                {(supplier.branches || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    Aucune agence enregistrée. Le fournisseur n&apos;a qu&apos;une seule implantation (siège).
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {supplier.branches.map((b, i) => (
                      <div
                        key={b.id || i}
                        className="border rounded-md p-3 space-y-1.5 text-sm bg-slate-50/60"
                        data-testid={`branch-row-${b.id || i}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-base">{b.name || "Agence sans nom"}</span>
                          {b.is_headquarters && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Siège social</Badge>
                          )}
                        </div>
                        {(b.address || b.postal_code || b.city) && (
                          <div className="text-muted-foreground">
                            {b.address}
                            {b.address && (b.postal_code || b.city) ? ", " : ""}
                            {b.postal_code} {b.city}
                          </div>
                        )}
                        {b.phone && <div><span className="text-muted-foreground">Tél. :</span> <span className="font-mono">{b.phone}</span></div>}
                        {b.email && <div><span className="text-muted-foreground">Email :</span> {b.email}</div>}
                        {b.website && (
                          <div>
                            <span className="text-muted-foreground">Site :</span>{" "}
                            <a href={b.website} target="_blank" rel="noreferrer" className="text-primary underline">{b.website}</a>
                          </div>
                        )}
                        {b.siret && <div><span className="text-muted-foreground">SIRET :</span> <span className="font-mono">{b.siret}</span></div>}
                        {b.tva_number && <div><span className="text-muted-foreground">N° TVA :</span> <span className="font-mono">{b.tva_number}</span></div>}
                        {b.notes && <div className="text-xs italic text-muted-foreground">{b.notes}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader><CardTitle>Personnes à contacter ({(supplier.contacts || []).length})</CardTitle></CardHeader>
              <CardContent>
                {(supplier.contacts || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">Aucun contact enregistré.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nom</TableHead>
                        <TableHead>Fonction</TableHead>
                        <TableHead>Agence</TableHead>
                        <TableHead>Téléphone</TableHead>
                        <TableHead>Email</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {supplier.contacts.map((c, i) => {
                        const branch = (supplier.branches || []).find((b) => b.id === c.branch_id);
                        return (
                          <TableRow key={c.id || i} data-testid={`contact-row-${i}`}>
                            <TableCell className="font-medium">{c.name}</TableCell>
                            <TableCell>{c.function || "-"}</TableCell>
                            <TableCell>
                              {branch ? (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  {branch.name || "Agence"}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-xs">—</span>
                              )}
                            </TableCell>
                            <TableCell className="font-mono">{c.phone || "-"}</TableCell>
                            <TableCell className="text-sm">{c.email || "-"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="products" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Catalogue produits</CardTitle>
              <Button size="sm" onClick={() => setProductDialog({ open: true, product: null })} data-testid="add-product-btn">
                <Plus className="h-4 w-4 mr-1" /> Ajouter un produit
              </Button>
            </CardHeader>
            <CardContent>
              {products.length === 0 ? (
                <p className="text-sm text-muted-foreground italic text-center py-6 bg-muted/30 rounded">
                  Aucun produit. Cliquez sur "Ajouter un produit" pour démarrer votre catalogue.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Référence</TableHead>
                      <TableHead>Nom</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Unité de réf.</TableHead>
                      <TableHead>Prix courant</TableHead>
                      <TableHead>Prix / unité réf.</TableHead>
                      <TableHead>Prix / usage</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((p) => (
                      <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                        <TableCell className="font-mono text-xs">{p.reference || "-"}</TableCell>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell>{p.product_type || "-"}</TableCell>
                        <TableCell><Badge variant="outline">{p.reference_unit}</Badge></TableCell>
                        <TableCell>
                          {p.current_price
                            ? <span>{formatCurrency(p.current_price.amount)} / {p.current_price.quantity} {p.current_price.unit}</span>
                            : <span className="text-xs text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>
                          {p.price_per_reference_unit != null
                            ? <span className="font-mono font-semibold">{formatCurrency(p.price_per_reference_unit)}/{p.reference_unit}</span>
                            : <span className="text-xs text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>
                          {p.price_per_usage_unit != null && p.consumption_per_unit
                            ? <span className="font-mono font-semibold text-indigo-700">{formatCurrency(p.price_per_usage_unit)}/{p.consumption_per_unit}</span>
                            : <span className="text-xs text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setPriceDialog({ open: true, product: p })} data-testid={`product-prices-${p.id}`} title="Historique des prix">
                              <Euro className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setStockDialog({ open: true, product: p })} data-testid={`product-stock-${p.id}`} title="Stock">
                              <Boxes className="h-4 w-4 text-amber-600" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setProductDialog({ open: true, product: p })} data-testid={`edit-product-${p.id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteProduct(p.id)} data-testid={`delete-product-${p.id}`}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Événements marquants</CardTitle>
              <Button size="sm" onClick={openCreateEvent} data-testid="add-sup-event-btn">
                <Plus className="h-4 w-4 mr-1" /> Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <p className="text-sm text-muted-foreground italic text-center py-6 bg-muted/30 rounded">
                  Aucun événement enregistré.
                </p>
              ) : (
                <div className="space-y-2">
                  {events.map((ev) => (
                    <div
                      key={ev.id}
                      className="flex items-start justify-between p-3 rounded border bg-card"
                      data-testid={`sup-event-${ev.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm flex-wrap">
                          <Badge className={SEVERITY_CONFIG[ev.severity]?.color || ""}>{SEVERITY_CONFIG[ev.severity]?.label || ev.severity}</Badge>
                          <span className="font-mono font-medium">
                            {new Date(ev.event_date).toLocaleDateString("fr-FR")}
                            {ev.event_time ? ` à ${ev.event_time}` : ""}
                          </span>
                          {ev.title && <span className="font-medium">{ev.title}</span>}
                        </div>
                        <p className="text-sm mt-1 whitespace-pre-wrap">{ev.comment}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditEvent(ev)} data-testid={`edit-sup-event-${ev.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteEvent(ev.id)} data-testid={`delete-sup-event-${ev.id}`}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="timeline" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" /> Chronologie
              </CardTitle>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <p className="text-sm text-muted-foreground italic text-center py-6 bg-muted/30 rounded">
                  Aucun événement à afficher.
                </p>
              ) : (
                <div className="relative border-l-2 border-border pl-4 space-y-3">
                  {events.map((ev) => (
                    <div key={ev.id} className="relative">
                      <span className={`absolute -left-[22px] top-1 h-3 w-3 rounded-full ring-4 ring-background ${
                        ev.severity === "positive" ? "bg-green-500" :
                        ev.severity === "negative" ? "bg-red-500" : "bg-primary"
                      }`} />
                      <div className="flex items-start gap-3">
                        <span className="text-xs font-mono tabular-nums text-muted-foreground min-w-[95px]">
                          {new Date(ev.event_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" })}
                          {ev.event_time ? ` · ${ev.event_time}` : ""}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={`${SEVERITY_CONFIG[ev.severity]?.color} text-xs`}>{SEVERITY_CONFIG[ev.severity]?.label}</Badge>
                            {ev.title && <span className="text-sm font-medium">{ev.title}</span>}
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5 whitespace-pre-wrap">{ev.comment}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="equipment" className="space-y-4">
          <SupplierLinkedEquipmentTab supplierId={supplier.id} />
        </TabsContent>
      </Tabs>

      <ProductFormDialog
        open={productDialog.open}
        product={productDialog.product}
        supplier={supplier}
        onClose={() => setProductDialog({ open: false, product: null })}
        onSaved={() => { setProductDialog({ open: false, product: null }); fetchAll(); }}
      />
      <ProductPricesDialog
        open={priceDialog.open}
        product={priceDialog.product}
        onClose={() => { setPriceDialog({ open: false, product: null }); fetchAll(); }}
      />
      <ProductStockDialog
        open={stockDialog.open}
        product={stockDialog.product}
        onClose={() => { setStockDialog({ open: false, product: null }); fetchAll(); }}
      />

      <Dialog open={eventDialog.open} onOpenChange={(v) => !v && setEventDialog({ open: false, event: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{eventDialog.event ? "Modifier l'événement" : "Nouvel événement marquant"}</DialogTitle>
            <DialogDescription>Consigner une interaction positive, neutre ou négative avec le fournisseur.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={eventForm.event_date} onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })} data-testid="sup-event-date" />
              </div>
              <div>
                <Label>Heure</Label>
                <Input type="time" value={eventForm.event_time} onChange={(e) => setEventForm({ ...eventForm, event_time: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={eventForm.severity} onValueChange={(v) => setEventForm({ ...eventForm, severity: v })}>
                <SelectTrigger data-testid="sup-event-severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="positive">Positif</SelectItem>
                  <SelectItem value="neutral">Neutre</SelectItem>
                  <SelectItem value="negative">Négatif</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Titre (optionnel)</Label>
              <Input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} />
            </div>
            <div>
              <Label>Commentaire *</Label>
              <Textarea rows={5} value={eventForm.comment} onChange={(e) => setEventForm({ ...eventForm, comment: e.target.value })} data-testid="sup-event-comment" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEventDialog({ open: false, event: null })}>Annuler</Button>
            <Button onClick={saveEvent} data-testid="save-sup-event-btn">{eventDialog.event ? "Enregistrer" : "Ajouter"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierDetailView;
